<script setup lang="ts">
import { computed } from 'vue';

import { useItineraryStore } from '@apps/main/modules/store/itineraryStore';
import { usePriceCalendarStore } from '@apps/main/new-desktop/core/stores/priceCalendar/priceCalendarStore';
import {
  PriceCalendarInner,
  PriceCalendarTheme,
} from '@apps/main/new-desktop/modules/priceCalendar';

import {
  AppSelect,
  AppOutInSmoothAppearTransition,
  AppCollapseTransition,
} from '@libs/ui';
import type { AppSelectOption } from '@libs/ui';

import { CoreEventSource } from '../models';

const priceCalendarStore = usePriceCalendarStore();
const itineraryStore = useItineraryStore();

interface FirstDayTripPriceCalendarMobileProps {
  showMonthSelectDropdown: boolean;
  showPriceCalendar: boolean;
  source: CoreEventSource;
  theme?: PriceCalendarTheme;
  shadow?: boolean;
}

const props = withDefaults(
  defineProps<FirstDayTripPriceCalendarMobileProps>(),
  {
    theme: PriceCalendarTheme.Dark,
  }
);

const isProductPage = computed(
  () =>
    props.source !== CoreEventSource.TripOverview &&
    props.source !== CoreEventSource.MonthsSelector &&
    props.source !== CoreEventSource.HotelSelection
);

function selectItineraryMonthFromAppSelectOption(
  selected: AppSelectOption<string>
): void {
  const date = new Date(selected.id);
  priceCalendarStore.selectItineraryMonth(date, props.source);
}
</script>

<template>
  <div class="first-day-trip-price-calendar-mobile" :class="props.theme">
    <div class="price-calendar-actions">
      <slot name="left-action" />
      <div v-if="props.showMonthSelectDropdown">
        <AppOutInSmoothAppearTransition>
          <AppSelect
            v-if="showPriceCalendar"
            :type="
              props.theme === PriceCalendarTheme.Dark
                ? 'transparent-white'
                : 'transparent-l'
            "
            size="size-s"
            :options="priceCalendarStore.monthsSelectOptions"
            :selected="priceCalendarStore.selectedMonthOption"
            @select="selectItineraryMonthFromAppSelectOption"
          />
          <slot v-else name="right-action" />
        </AppOutInSmoothAppearTransition>
      </div>
    </div>
    <!--TODO: Hardcoded value for mobile calendar, find a way to not hardcode this -->
    <AppCollapseTransition height="96px">
      <div v-if="props.showPriceCalendar" class="price-calendar-container">
        <PriceCalendarInner
          :months="priceCalendarStore.priceCalendarMonths"
          :allow-hover-actions="priceCalendarStore.allowHoverActions"
          :selected-itinerary-date="itineraryStore.selectedItinerary?.date"
          :is-product-page="isProductPage"
          :is-small-device="true"
          @select-date="
            priceCalendarStore.selectItineraryDate($event, props.source)
          "
          @select-month="
            priceCalendarStore.selectItineraryMonth($event, props.source)
          "
        />
      </div>
    </AppCollapseTransition>
    <div class="bottom-row-container">
      <slot name="bottom-row" />
    </div>
  </div>
</template>

<style scoped lang="scss">
//NOTE: be careful, changes to this file may break the price calendar animation.
.first-day-trip-price-calendar-mobile {
  position: relative;

  &.light {
    .price-calendar-actions,
    .price-calendar-container {
      background-color: var(--color-white);
      color: var(--color-secondary-40);
    }
  }

  .price-calendar-actions {
    background-color: var(--color-primary-5);
    position: relative;
    z-index: 3;
    display: flex;
    justify-content: space-between;
    padding: var(--spacing-12) var(--spacing-16);
  }

  .price-calendar-container {
    background-color: var(--color-primary-5);
    position: relative;
    z-index: 1;
    padding-bottom: 0px;
    box-shadow: var(--shadow);

    .price-calendar {
      padding: 0 var(--spacing-16) var(--spacing-8) var(--spacing-16);
    }
  }

  .bottom-row-container {
    position: relative;
    z-index: 2;
  }
}
</style>
